import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "../lib/api";
import { BANCHINA_ROLE } from "../lib/roles";
import { editLink, settoreLabel, shiftApi } from "../lib/settori";
import { usePerm } from "../context/PermContext";
import { Pencil, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loading, Panel } from "./common";
import { Tabs, TabsList, TabsTrigger } from "./ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

const MESI = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const STATUS = {
  green: { cls: "bg-emerald-500 text-white", label: "Tutti i report inviati" },
  yellow: { cls: "bg-amber-400 text-[var(--navy)]", label: "Report parziali" },
  red: { cls: "bg-red-500 text-white", label: "Report mancanti" },
  pending: { cls: "bg-slate-100 text-[var(--navy)] ring-1 ring-inset ring-slate-300", label: "Turno previsto" },
  none: { cls: "bg-white text-slate-400 ring-1 ring-inset ring-slate-200", label: "Nessun turno" },
};

export const VIG_TAB = "Vigilanza GPG";
const tabLabel = (s) => (s === VIG_TAB ? "Vigilanza Fiduciario e GPG" : settoreLabel(s));
const VIG_STATUS = {
  green: { cls: "bg-emerald-500 text-white", label: "Un report per ogni turno" },
  yellow: { cls: "bg-amber-400 text-[var(--navy)]", label: "Report parziali" },
  red: { cls: "bg-red-500 text-white", label: "Nessun report" },
  pending: { cls: "bg-slate-100 text-[var(--navy)] ring-1 ring-inset ring-slate-300", label: "Turni in corso" },
  none: { cls: "bg-white text-slate-400 ring-1 ring-inset ring-slate-200", label: "Nessun dato" },
};

const VigReport = ({ r }) => (
  <Panel className="!p-4" data-testid={`calendar-vig-report-${r.id}`}>
    <p className="font-semibold text-[var(--navy)]">{r.author_name} <span className="tag tag-teal ml-1">{r.turno}</span></p>
    {(r.servizio || r.profilo) && <p className="text-xs text-slate-500">{r.servizio || r.profilo}</p>}
    {r.mezzo && <p className="text-sm text-slate-600 mt-1"><b>Mezzo:</b> {r.mezzo}</p>}
    <p className="text-sm text-slate-600 mt-1 whitespace-pre-wrap"><b>Segnalazioni:</b> {r.segnalazione || "—"}</p>
    {r.provvedimenti && <p className="text-sm text-slate-600 mt-1 whitespace-pre-wrap"><b>Provvedimenti:</b> {r.provvedimenti}</p>}
    {r.immagini?.length > 0 && (
      <div className="grid grid-cols-3 gap-2 mt-3" data-testid={`calendar-vig-photos-${r.id}`}>
        {r.immagini.map((src, i) => (
          <a key={i} href={src} target="_blank" rel="noreferrer" onClick={(e) => { e.preventDefault(); const w = window.open(); if (w) { w.document.write(`<img src="${src}" style="max-width:100%">`); } }} data-testid={`calendar-vig-photo-${r.id}-${i}`}>
            <img src={src} alt={`Foto ${i + 1}`} className="w-full aspect-square object-cover rounded-lg ring-1 ring-slate-200 hover:opacity-90 transition-opacity" />
          </a>))}
      </div>)}
  </Panel>
);

export async function deleteShiftReport(r) {
  if (!window.confirm(`Cancellare il report di ${r.author_name} del ${r.data}?`)) return false;
  try { await api(`${shiftApi(r.settore)}/${r.id}`, { method: "DELETE" }); toast.success("Report cancellato"); return true; }
  catch (e) {
    if (e.status === 404 || e.status === 405) toast.error("Cancellazione non ancora disponibile: va aggiunta sul server dell'app mobile");
    else toast.error(e.message);
    return false;
  }
}

export const DeleteReportButton = ({ r, onDone }) => {
  const { can } = usePerm();
  if (!can("report.delete")) return null;
  return <button onClick={async () => { if (await deleteShiftReport(r)) onDone?.(); }} className="text-red-600 hover:bg-red-50 rounded-full p-1.5" title="Cancella report" data-testid={`delete-report-${r.id}`}><Trash2 className="w-4 h-4" /></button>;
};

const EditReportLink = ({ r }) => {
  const { can, viewUser } = usePerm();
  if (!can("report.edit") || !viewUser || r.author_id !== viewUser.id) return null;
  return <Link to={editLink(r)} className="text-[var(--teal)] hover:bg-[var(--teal-soft)] rounded-full p-1.5" title="Modifica report" data-testid={`edit-report-${r.id}`}><Pencil className="w-4 h-4" /></Link>;
};

const DayDialog = ({ day, vig, onClose, onChanged }) => (
  <Dialog open={!!day} onOpenChange={(v) => !v && onClose()}>
    <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="report-calendar-day-dialog">
      <DialogHeader><DialogTitle>{day && new Date(day.data).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })}</DialogTitle></DialogHeader>
      {day && (<>
        <span className={`w-fit tag ${(vig ? VIG_STATUS : STATUS)[day.status]?.cls}`}>{(vig ? VIG_STATUS : STATUS)[day.status]?.label}</span>
        {vig && day.scheduled?.length > 0 && <p className="text-sm text-slate-600" data-testid="calendar-vig-turni">Turni {day.festivo ? "festivi" : "feriali"}: {day.scheduled.join(", ")} · coperti: {day.reported?.length ? day.reported.join(", ") : "nessuno"}</p>}
        {vig && day.reports?.map((r) => <VigReport key={r.id} r={r} />)}
        {!vig && day.scheduled?.length > 0 && <p className="text-sm text-slate-600">Turni previsti: {day.scheduled.join(", ")} · inviati: {day.reported?.length ? day.reported.join(", ") : "nessuno"}</p>}
        {!vig && day.reports?.map((r) => (
          <Panel key={r.id} className="!p-4" data-testid={`calendar-report-${r.id}`}>
            <div className="flex items-start justify-between gap-2"><p className="font-semibold text-[var(--navy)]">{r.author_name} <span className="tag tag-teal capitalize ml-1">{r.turno}</span></p><span className="flex"><EditReportLink r={r} /><DeleteReportButton r={r} onDone={onChanged} /></span></div>
            <p className="text-sm text-slate-600 mt-1 whitespace-pre-wrap"><b>Effettuate:</b> {r.attivita_svolte || "—"}</p>
            {r.attivita_da_svolgere && <p className="text-sm text-slate-600 mt-1 whitespace-pre-wrap"><b>Da svolgere:</b> {r.attivita_da_svolgere}</p>}
          </Panel>))}
        {day.personale?.length > 0 && <div><p className="text-xs uppercase tracking-wider text-slate-500 mb-1">Personale in turno</p>
          {day.personale.map((p, i) => <p key={i} className="text-sm text-slate-700">{p.nome} · {p.turno} {p.orario}</p>)}</div>}
      </>)}
    </DialogContent>
  </Dialog>
);

export function ReportCalendar({ settori }) {
  const now = new Date();
  const [settore, setSettore] = useState(settori[0]);
  useEffect(() => { if (!settori.includes(settore)) setSettore(settori[0]); }, [settori, settore]);
  const [p, setP] = useState({ mese: now.getMonth() + 1, anno: now.getFullYear() });
  const [d, setD] = useState(null);
  const [day, setDay] = useState(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!settore) { return undefined; }
    let live = true;
    setD(null);
    const url = settore === VIG_TAB ? `/vig-reports/calendar?mese=${p.mese}&anno=${p.anno}`
      : `${shiftApi(settore)}/calendar?settore=${encodeURIComponent(settore)}&mese=${p.mese}&anno=${p.anno}`;
    api(url).then((r) => { if (live) setD(r); }).catch(() => { if (live) setD({ days: [] }); });
    return () => { live = false; };
  }, [settore, p, tick]);
  if (!settore) return null;
  const vig = settore === VIG_TAB;
  const S = vig ? VIG_STATUS : STATUS;
  const pick = (x) => {
    const s = x.status;
    if (vig) return VIG_STATUS[s] ? s : "none";
    if (settore !== BANCHINA_ROLE) return s === "yellow" ? "red" : s;
    const sent = new Set((x.reports || []).map((r) => r.turno));
    return (s === "pending" || s === "red") && sent.size > 0 && (x.scheduled || []).some((t) => !sent.has(t)) ? "yellow" : s;
  };
  const legend = Object.entries(S).filter(([k]) => vig || settore === BANCHINA_ROLE || k !== "yellow");
  const move = (n) => { const m = p.mese + n; setP({ mese: ((m + 11) % 12) + 1, anno: p.anno + Math.floor((m - 1) / 12) }); };
  const offset = (new Date(p.anno, p.mese - 1, 1).getDay() + 6) % 7;
  return (
    <Panel className="mb-10" data-testid="report-calendar">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        {settori.length > 1 ? <Tabs value={settore} onValueChange={setSettore}><TabsList className="flex-wrap h-auto">{settori.map((s) => <TabsTrigger key={s} value={s} data-testid={`report-calendar-tab-${tabLabel(s).toLowerCase().replace(/\s+/g, "-")}`}>{tabLabel(s)}</TabsTrigger>)}</TabsList></Tabs>
          : <h2 className="font-display text-xl font-bold text-[var(--navy)]">Calendario report</h2>}
        <div className="flex items-center gap-2">
          <button onClick={() => move(-1)} className="p-2 rounded-full hover:bg-slate-100" data-testid="report-calendar-prev" aria-label="Mese precedente"><ChevronLeft className="w-4 h-4" /></button>
          <span className="font-semibold text-[var(--navy)] w-36 text-center" data-testid="report-calendar-month">{MESI[p.mese - 1]} {p.anno}</span>
          <button onClick={() => move(1)} className="p-2 rounded-full hover:bg-slate-100" data-testid="report-calendar-next" aria-label="Mese successivo"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-1.5 text-center text-xs font-semibold text-slate-500 mb-1.5">{["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"].map((g) => <span key={g}>{g}</span>)}</div>
      {!d ? <Loading /> : (
        <div className="grid grid-cols-7 gap-1.5">
          {Array.from({ length: offset }, (_, i) => <span key={`e${i}`} />)}
          {d.days.map((x) => (
            <button key={x.data} onClick={() => setDay({ ...x, status: pick(x) })} className={`aspect-square sm:aspect-auto sm:h-14 rounded-xl text-sm font-semibold flex flex-col items-center justify-center transition-transform hover:scale-105 ${S[pick(x)]?.cls || S.none.cls} ${x.data === now.toISOString().slice(0, 10) ? "outline outline-2 outline-[var(--navy)]" : ""}`} data-testid={`report-calendar-day-${x.day}`}>
              {x.day}{x.reports?.length > 0 && <span className="text-[10px] font-normal opacity-90 hidden sm:block">{x.reports.length} report</span>}
            </button>))}
        </div>)}
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-4 text-xs text-slate-600">{legend.map(([k, v]) => <span key={k} className="flex items-center gap-1.5"><span className={`w-3 h-3 rounded ${v.cls}`} />{v.label}</span>)}</div>
      <DayDialog day={day} vig={vig} onClose={() => setDay(null)} onChanged={() => { setDay(null); setTick((t) => t + 1); }} />
    </Panel>
  );
}
